import { useLocation } from 'react-router-dom';

import { bottomNavigationStore } from '~/stores/BottomNavigationStore';
import type { Category } from '~/stores/CategoriesStore';

interface CategoryRef {
  slug?: string;
  ids: number[];
}

interface ActiveZone {
  /** The zone (top-level category) the open page belongs to, if any. */
  zone?: Category;
  /**
   * Whether the open page is the zone's own landing, not a page inside it;
   * the zone's catalog (`showProducts`) counts as a page inside.
   */
  isLanding: boolean;
}

const containsCategory = (category: Category, ref: CategoryRef): boolean =>
  String(category.id) === ref.slug ||
  ref.ids.includes(category.numberId) ||
  (category.subcategory ?? []).some((child) => containsCategory(child, ref));

/** The category the open page belongs to: a catalog page or a product's own category. */
const useOpenCategory = (): CategoryRef | null => {
  const { pathname } = useLocation();
  const [, section, slug] = pathname.split('/');

  if (section === 'c' && slug) {
    return { slug, ids: [] };
  }

  const product =
    section === 'p' ? bottomNavigationStore.product?.data : undefined;
  if (!product) {
    return null;
  }

  return {
    slug: product.categorySlug || undefined,
    ids: [product.categoryId, product.parentCategoryId].filter((id) => id > 0),
  };
};

/**
 * The zone whose subtree holds the open catalog page or the open product,
 * and whether that page is the zone landing itself.
 */
export const useActiveZone = (zones: readonly Category[]): ActiveZone => {
  const { search } = useLocation();
  const ref = useOpenCategory();

  if (!ref) {
    return { isLanding: false };
  }

  const zone = zones.find((candidate) => containsCategory(candidate, ref));
  const showsCatalog =
    new URLSearchParams(search).get('showProducts') === 'true';

  return {
    zone,
    isLanding: !!zone && String(zone.id) === ref.slug && !showsCatalog,
  };
};
